import { last } from "lodash";
import { convertBytesToMB, pluralize } from "../../utils";
import { consts, enums } from "../../shim/jsbridge";
import { YaraTestRunGridData } from "./@types/yaratestrunstate";
import {
  IYaraRuleset,
  YaraSample,
  YaraSampleRowGridItem,
  YaraTestRunData,
  YaraTestRunItemType,
  YaraTestRunMenuItems
} from "./@types";
import { getClassificationByValue } from "../../lib/enums/ThreatStatusEnum";
import { LOCATION } from "../search/consts";
import { YARA_REPO_PREFERENCES } from "./yara_repositories/consts";

const { data_upload_max_memory_size: dataUploadMaxMemorySize } = consts;

const warningRegex = /line (?<line>\d+):(?<msg>[^\n]+)/;

const handleTooLargeContent = (
  contentSizeInBytes: number,
  callback: ({ show, msg }: { show: boolean; msg: string }) => void
) => {
  const limitSizeInMB = convertBytesToMB(dataUploadMaxMemorySize);
  const overflow = contentSizeInBytes - dataUploadMaxMemorySize;
  callback({
    show: true,
    msg: `Limit is ${limitSizeInMB} ${pluralize(
      "megabyte",
      Number(limitSizeInMB)
    )}. You are over the limit by ${overflow} ${pluralize(
      "character",
      overflow
    )}`
  });
};

const extractWarnings = (warnings: string[]) =>
  (warnings || []).map(w => {
    const { line, msg } = (w.match(warningRegex)?.groups || {}) as {
      line: string;
      msg: string;
    };

    // There is a scenario where there is no line: in message
    // then we set it to first line and show message
    if (!line && !msg) return { msg: w, line: 1 };
    return { msg, line: parseInt(line, 10) };
  });

const getMatchedCount = (samples: YaraTestRunGridData) =>
  samples.map(sample => last(sample.matches || [])).filter(Boolean).length || 0;

const mapProcessingStateIdToLabel = (processingStateId: number): string => {
  switch (processingStateId) {
    case enums.state.UNPROCESSED:
      return "UNPROCESSED";
    case enums.state.PROCESSING:
      return "PROCESSING";
    case enums.state.PROCESSED:
      return "PROCESSED";
    case enums.state.ERROR:
      return "ERROR";
    default:
      return "UNKNOWN";
  }
};

const mapYaraSampleToRowGridItem = (
  sample: YaraSample,
  rulesetName?: string
): YaraSampleRowGridItem => ({
  ...sample,
  threat_status: getClassificationByValue(sample.threat_status),
  location: sample.cloud ? LOCATION.CLOUD : LOCATION.LOCAL,
  sample_size: sample.file_size,
  sample_available: sample.downloadable,
  sample_type: sample.type_display,
  string_submissions_user: sample.username ? [sample.username] : [],
  string_sample_processing_status: mapProcessingStateIdToLabel(sample.state),
  integer_submissions: sample.id ? 1 : 0,
  file_count: (sample.file_count ?? 0) + 1,
  date_submission_time: sample.submission_date,
  file_name: sample.filename,
  rulesetName
});

const isInStateThatNeedsUpdating = (ruleset: IYaraRuleset) =>
  ruleset.retrohunt_status === enums.yaraRetroHuntStatus.RUNNING ||
  ruleset.retrohunt_status === enums.yaraRetroHuntStatus.VALIDATING ||
  (ruleset.cloud && ruleset.cloud_status === enums.yaraRulesetStatus.PENDING) ||
  ruleset.scale_status === enums.yaraRulesetStatus.PENDING;

function getMessageForAction(
  isSuccess: boolean,
  actionName: string,
  message?: string
): string {
  if (!isSuccess) {
    return message ?? "Error occurred while executing action.";
  } else {
    switch (actionName) {
      case enums.yaraRulesetActions.RUNRETROHUNT:
        return "Retro hunt started.";
      case enums.yaraRulesetActions.STOPRETROHUNT:
        return "Retro hunt stopped.";
      case enums.yaraRulesetActions.CLEARRETROHUNT:
        return "Retro hunt results cleared.";
      case enums.yaraRulesetActions.ENABLE:
        return "Ruleset enabled!";
      case enums.yaraRulesetActions.DISABLE:
        return "Ruleset disabled!";
      case enums.yaraRulesetActions.DELETE:
        return "Ruleset deleted!";
      case enums.yaraRulesetActions.PUBLISH:
        return "Publish started.";
      default:
        return "Action successfully executed.";
    }
  }
}

function getMessageForBulkAction(actionName: string, numberOfRulesets: number) {
  switch (actionName) {
    case enums.yaraRulesetActions.RUNRETROHUNT:
      return `Retro hunt started on ${numberOfRulesets} rulesets.`;
    case enums.yaraRulesetActions.STOPRETROHUNT:
      return `Retro hunt stopped on ${numberOfRulesets} rulesets.`;
    case enums.yaraRulesetActions.CLEARRETROHUNT:
      return `Retro hunt results cleared on ${numberOfRulesets} rulesets.`;
    case enums.yaraRulesetActions.ENABLE:
      return `${numberOfRulesets} rulesets enabled.`;
    case enums.yaraRulesetActions.DISABLE:
      return `${numberOfRulesets} rulesets disabled.`;
    case enums.yaraRulesetActions.DELETE:
      return `${numberOfRulesets} rulesets deleted.`;
    case enums.yaraRulesetActions.PUBLISH:
      return `Publish started on ${numberOfRulesets} rulesets.`;
    default:
      return `Action successfully executed on ${numberOfRulesets} rulesets.`;
  }
}

export const getYaraRepoPreferenceLabelFromEnum = (
  preferenceEnum: number | undefined
) => {
  if (preferenceEnum == null) return undefined;
  return YARA_REPO_PREFERENCES[preferenceEnum].label;
};

const getRepositoryNameFromUrl = (url: string) => {
  const regex = /https?:\/\/[^/]+\/(.*)/;
  const match = url.match(regex);
  return match ? match[1] : "";
};

function convertYaraTestRunDataToRequest(
  data: YaraTestRunMenuItems
): YaraTestRunData {
  return data.reduce(
    (previousValue, currentValue) => {
      switch (currentValue.type) {
        case YaraTestRunItemType.HASH:
          previousValue.hashes.push(currentValue.value);
          break;
        case YaraTestRunItemType.TAG:
          previousValue.tags.push(currentValue.value);
          break;
        default:
          break;
      }

      return previousValue;
    },
    { hashes: [] as string[], tags: [] as string[] }
  );
}

function formatHashString(hash: string) {
  return `${hash.slice(0, 5)}...${hash.slice(-3)}`;
}

export {
  handleTooLargeContent,
  extractWarnings,
  getMatchedCount,
  mapYaraSampleToRowGridItem,
  isInStateThatNeedsUpdating,
  getMessageForAction,
  getMessageForBulkAction,
  getRepositoryNameFromUrl,
  convertYaraTestRunDataToRequest,
  formatHashString
};
